import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { CorrectionService } from '@app/reducers/orm/correction/correction.service';
import { Store } from '@ngrx/store';
import { _, TranslateService } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { toISODateString } from '@shiftbase-com/ts-shared/utilities';
import { take } from 'rxjs';

import { countOutOfBalance, getEmployeeIdsForBalanceStep } from './payroll-balances';
import { subTextKeyFor } from './payroll-progress';
import { PayrollRunService } from './payroll-run.service';
import { type PayrollRunScope } from './payroll.model';

/**
 * Plus/min balances for the payroll run, and the count the balance step reports as its backlog.
 *
 * The balances are read as soon as the run has a scope rather than when the manager reaches step
 * three, so a period with nothing left to balance has already resolved the step by the time they
 * get there and the flow carries them past it.
 *
 * The period's contracts, which decide who holds one and so who this step lists at all, are loaded
 * by `usePayrollScopeData` along with the rest of the run's data. Nothing else in the app loads
 * them — startup covers employees and contract *types* only — so until that request answers, no
 * employee looks like a contract holder and a period full of unsettled balances would report itself
 * complete. `onContractsRequested`/`onContractsSettled` are how the loader keeps this honest.
 *
 * A plain signals service like the rest of the payroll flow. Balances are read for the period's
 * end date only: that is the date the step corrects on, so a mid-period snapshot would report a
 * backlog the screen cannot act on.
 */
@Injectable({ providedIn: 'root' })
export class PayrollBalancesService {
  #store = inject<Store<AppState>>(Store);
  #corrections = inject(CorrectionService);
  #run = inject(PayrollRunService);
  #translate = inject(TranslateService);

  // null while no answer is in hand — either nothing has been asked for yet, or a request is
  // still out. Distinct from `{}`, which is a real answer meaning nothing needs balancing.
  #balances = signal<Record<string, number> | null>(null);

  // Until the period's contracts are in the store, `employeeIds` is not an answer — it is an
  // artefact of an empty store.
  #contractsLoaded = signal(false);

  // Whether the contract request has finished, successfully or not. Distinct from
  // `#contractsLoaded`: a failed load leaves the count unresolved (so the step cannot report
  // itself complete) but must not leave a host waiting on an employee set that will never come.
  #contractsSettled = signal(false);
  contractsSettled = this.#contractsSettled.asReadonly();

  // The employee set and date a balance request was last made for. `employeeIds` recomputes into a
  // fresh array on any store change, so identity alone would re-fire the request on unrelated
  // writes.
  #requestedFor: string | null = null;

  /**
   * `store.selectSignal` inside the computed for the same reason `usePayrollStepSources` does it: the
   * selector is parameterised by the run's scope, so a new scope needs a new selector instance.
   */
  employeeIds = computed<string[]>(() => {
    const scope = this.#run.scope();

    return scope ? this.#store.selectSignal(getEmployeeIdsForBalanceStep(scope))() : [];
  });

  /**
   * Zero means 'complete' to the progress model, which ticks the step and advances the manager past
   * it — so nothing may report zero until the numbers behind it are real. Until then the count is
   * the employees known so far, floored at one so an as-yet-empty store cannot read as a settled
   * period. Over-reporting is recoverable; a false all-clear is not.
   */
  outstanding = computed(() => {
    const balances = this.#balances();

    if (balances !== null && this.#contractsLoaded()) {
      return countOutOfBalance(balances);
    }

    return this.#run.scope() === null ? 0 : Math.max(this.employeeIds().length, 1);
  });

  subText = computed(() => {
    if (this.#balances() === null || !this.#contractsLoaded()) {
      return this.#translate.instant(_('Checking plus/min balances'));
    }

    const count = this.outstanding();
    const key = subTextKeyFor(count, {
      zero: _('No balances to correct'),
      one: _('[count] employee out of balance'),
      many: _('[count] employees out of balance'),
    });

    return this.#translate.instant(key, { count });
  });

  constructor() {
    effect(() => {
      const scope = this.#run.scope();
      const employeeIds = this.employeeIds();
      // Asking before the contracts answer would only ask for whoever an earlier page left in the
      // store, and the answer could not be trusted anyway — `outstanding` ignores it until then.
      const contractsLoaded = this.#contractsLoaded();

      if (scope && !contractsLoaded) {
        return;
      }

      untracked(() => this.#loadBalancesFor(scope, employeeIds));
    });
  }

  /** Re-reads the balances after the manager saves a correction from the step. */
  refresh(): void {
    this.#requestedFor = null;

    if (this.#contractsLoaded()) {
      this.#loadBalancesFor(this.#run.scope(), this.employeeIds());
    }
  }

  /** Called by `usePayrollScopeData`, which owns the contract request, as it goes out. */
  onContractsRequested(): void {
    this.#contractsLoaded.set(false);
    this.#contractsSettled.set(false);
  }

  /**
   * Called by `usePayrollScopeData` when the contract request answers. `loaded` false means it
   * failed: the step stops waiting for an employee set that is not coming, but its count stays
   * unresolved rather than resolving on a store nobody filled.
   */
  onContractsSettled(loaded: boolean): void {
    this.#contractsLoaded.set(loaded);
    this.#contractsSettled.set(true);
  }

  #loadBalancesFor(scope: PayrollRunScope | null, employeeIds: string[]): void {
    if (!scope) {
      this.#requestedFor = null;
      this.#balances.set(null);
      // Leaving the flow drops the contract answer with everything else, so a fresh run does not
      // start out believing the previous period's employee set is already settled.
      this.#contractsLoaded.set(false);
      this.#contractsSettled.set(false);
      return;
    }

    const date = toISODateString(scope.range.end);
    const requestKey = [date, ...employeeIds].join('|');

    if (requestKey === this.#requestedFor) {
      return;
    }

    this.#requestedFor = requestKey;
    this.#balances.set(null);

    this.#corrections
      .getBalance('Overtime', date, ...employeeIds)
      .pipe(take(1))
      .subscribe({
        next: (balances) => this.#balances.set(balances),
        // The error toast is the interceptor's, which taps without swallowing — so this has to
        // absorb it rather than let it surface as an unhandled rejection. Leaving `#balances` null
        // keeps the step reporting its employees as outstanding rather than resolving on a number
        // nobody has.
        error: () => undefined,
      });
  }
}
